import React, { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export default function EquityCurve({ tradeHistory, account, t, isDark, glassCard }) {
  const data = useMemo(() => {
    if (!tradeHistory || tradeHistory.length === 0) {
      const bal = account?.balance || 10000;
      return [
        { label: 'Now', equity: bal, dayPnL: 0, trades: 0 },
      ];
    }

    // Group trades by day
    const sorted = [...tradeHistory].sort((a, b) => new Date(a.time || 0) - new Date(b.time || 0));
    const dayMap = new Map();
    sorted.forEach(trade => {
      const d = trade.time ? new Date(trade.time) : new Date();
      const key = d.toISOString().slice(0, 10);
      if (!dayMap.has(key)) dayMap.set(key, { profit: 0, trades: 0, date: d });
      const entry = dayMap.get(key);
      entry.profit += trade.profit || 0;
      entry.trades += 1;
    });

    const totalProfit = tradeHistory.reduce((s, d) => s + (d.profit || 0), 0);
    const startBalance = (account?.balance || 10000) - totalProfit;
    let running = startBalance;
    const points = [];

    for (const [, day] of dayMap) {
      running += day.profit;
      const label = day.date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      points.push({
        label,
        equity: parseFloat(running.toFixed(2)),
        dayPnL: parseFloat(day.profit.toFixed(2)),
        trades: day.trades,
      });
    }

    return points;
  }, [tradeHistory, account]);

  const totalDays = data.length;
  const minEquity = Math.min(...data.map(d => d.equity));
  const maxEquity = Math.max(...data.map(d => d.equity));
  const padding = (maxEquity - minEquity) * 0.1 || 100;
  const isUp = data.length >= 2 ? data[data.length - 1].equity >= data[0].equity : true;
  const totalChange = data.length >= 2 ? data[data.length - 1].equity - data[0].equity : 0;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]) return null;
    const d = payload[0].payload;
    return (
      <div style={{
        background: isDark ? 'rgba(15,23,42,0.92)' : 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)', border: `1px solid ${t.cardBorder}`,
        borderRadius: 12, padding: '10px 14px', fontSize: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{d.label}</div>
        <div style={{ color: t.textMuted }}>
          Equity: <span style={{ color: t.text, fontWeight: 600, fontFamily: 'monospace' }}>${d.equity.toLocaleString()}</span>
        </div>
        <div style={{ color: d.dayPnL >= 0 ? t.positive : t.negative, fontWeight: 600, fontFamily: 'monospace', marginTop: 2 }}>
          Day P&L: {d.dayPnL >= 0 ? '+' : ''}{d.dayPnL.toFixed(2)}
        </div>
        <div style={{ color: t.textFaint, fontSize: 11, marginTop: 2 }}>{d.trades} trade{d.trades !== 1 ? 's' : ''}</div>
      </div>
    );
  };

  return (
    <div style={{ ...glassCard, padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Equity Curve</span>
          {data.length >= 2 && (
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
              background: isUp ? (isDark ? 'rgba(125,249,198,0.14)' : 'rgba(15,157,122,0.10)') : (isDark ? 'rgba(244,63,94,0.15)' : 'rgba(244,63,94,0.10)'),
              color: isUp ? t.positive : t.negative,
              border: `1px solid ${isUp ? (isDark ? 'rgba(125,249,198,0.28)' : 'rgba(15,157,122,0.18)') : (isDark ? 'rgba(244,63,94,0.25)' : 'rgba(244,63,94,0.20)')}`,
            }}>
              {isUp ? '+' : ''}{totalChange.toFixed(2)}
            </span>
          )}
        </div>
        <span style={{ fontSize: 11, color: t.textFaint }}>{totalDays} day{totalDays !== 1 ? 's' : ''}</span>
      </div>

      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
            <defs>
              <linearGradient id="equityGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={isUp ? t.positive : t.negative} stopOpacity={0.3} />
                <stop offset="95%" stopColor={isUp ? t.positive : t.negative} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: t.textFaint }}
              axisLine={{ stroke: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[minEquity - padding, maxEquity + padding]}
              tick={{ fontSize: 10, fill: t.textFaint }}
              axisLine={false}
              tickLine={false}
              tickFormatter={v => `$${v.toLocaleString()}`}
              width={70}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="equity"
              stroke={isUp ? t.positive : t.negative}
              strokeWidth={2}
              fill="url(#equityGrad)"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: isUp ? t.positive : t.negative, fill: isDark ? '#060918' : '#f0f2f7' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
